import React, { useEffect, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'

interface ProtectedRouteProps {
  children: React.ReactNode
}

// Страницы, доступные при token auth (ограниченный режим)
// '/' в начале открывает доступ ко всем маршрутам для token auth пользователей
export const TOKEN_ACCESSIBLE_PATHS = [
  '/',
  '/critical-events',
  '/crime-application',
  '/crime-statistics',
  '/investors',
  '/admin-violations-v2',
  '/summary',
]

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isTokenAuth, isTestModeAuth, loginWithToken } = useAuth()
  const location = useLocation()
  const [isProcessingToken, setIsProcessingToken] = useState(false)

  const searchParams = new URLSearchParams(location.search)
  const isMobile = searchParams.get('is_mobile') === 'true'
  const token = searchParams.get('token')

  // Проверяем, является ли текущий путь доступным по токену
  const isTokenAccessiblePath = TOKEN_ACCESSIBLE_PATHS.some((path) =>
    location.pathname.startsWith(path)
  )

  // Проверяем токен из URL для mobile или для страниц доступных по токену
  useEffect(() => {
    const shouldLoginWithToken =
      token && !isAuthenticated && (isMobile || isTokenAccessiblePath)

    if (shouldLoginWithToken) {
      setIsProcessingToken(true)

      // Асинхронно логинимся по токену
      loginWithToken(token).finally(() => {
        setIsProcessingToken(false)
      })
    }
  }, [location.search, location.pathname, isAuthenticated, loginWithToken, token, isMobile, isTokenAccessiblePath])

  // Пока обрабатываем токен - показываем загрузку
  if (isProcessingToken) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-blue-500 border-solid"></div>
      </div>
    )
  }

  // Разрешаем доступ если:
  // 1. TEST_MODE - полный доступ без авторизации
  // 2. Пользователь авторизован через обычный логин (полный доступ)
  // 3. Есть токен для mobile (полный доступ)
  // 4. Авторизован по токену - доступ к 5 разрешенным страницам

  // TEST_MODE: полный доступ ко всем страницам
  if (isTestModeAuth) {
    return <>{children}</>
  }

  if (isAuthenticated) {
    // Если авторизован по токену - проверяем доступ к разрешенным страницам
    if (isTokenAuth && !isMobile) {
      // Проверяем, находится ли текущий путь в списке разрешенных
      const isAllowedPath = TOKEN_ACCESSIBLE_PATHS.some((path) =>
        location.pathname.startsWith(path)
      )

      if (!isAllowedPath) {
        // Редирект на первую разрешенную страницу
        return <Navigate to="/critical-events" replace />
      }
    }
    // Обычная авторизация или mobile - полный доступ
    return <>{children}</>
  }

  // Не авторизован - проверяем токен в URL
  const hasTokenAccess = token && (isMobile || isTokenAccessiblePath)

  if (!hasTokenAccess) {
    // Сохраняем URL параметры при редиректе на /login
    const redirectPath = `/login${location.search}`
    return <Navigate to={redirectPath} replace state={{ from: location }} />
  }

  return <>{children}</>
}
